import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, pdfUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { money } from "@/lib/brands";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ClientPetPicker } from "@/components/ClientPetPicker";
import { CloseFlow } from "@/components/CloseFlow";
import { Plus, Download, Stethoscope, Pencil, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Consultations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pmap, setPmap] = useState({});
  const [loading, setLoading] = useState(true);
  const [closeRec, setCloseRec] = useState(null);
  const isAdmin = user?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/consultations");
      setItems(data);
      const ids = [...new Set(data.map((r) => r.patient_id))];
      if (ids.length) {
        const { data: pts } = await api.get("/patients");
        const m = {}; pts.forEach((p) => (m[p.id] = p)); setPmap(m);
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2"><Stethoscope className="h-6 w-6 text-primary" /><h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">Vet Consultation</h1></div>
          <p className="text-sm text-muted-foreground mt-1">Create and manage medical consultations. Set the amount to close, then generate the report.</p>
        </div>
        <ClientPetPicker
          trigger={<Button data-testid="new-consultation-btn"><Plus className="h-4 w-4 mr-2" />New Vet Consultation</Button>}
          onPick={(p) => navigate(`/consultations/new?patient=${p.id}`)}
        />
      </div>

      <Card className="p-0 border-border/60 overflow-hidden">
        {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && <div className="p-12 text-center text-muted-foreground"><Stethoscope className="h-10 w-10 mx-auto opacity-40 mb-3" />No consultations yet.</div>}
        <div className="divide-y divide-border">
          {items.map((r) => {
            const p = pmap[r.patient_id] || {};
            const closed = r.amount != null && r.amount !== "";
            return (
              <div key={r.id} data-testid={`consultation-row-${r.id}`} className="flex items-center justify-between gap-4 px-5 py-4 hover:bg-muted/40 transition-colors">
                <button onClick={() => navigate(`/consultations/${r.id}/edit`)} className="text-left min-w-0 flex-1">
                  <div className="font-medium truncate">{p.pet_name || "—"} <span className="text-muted-foreground font-normal">· {p.owner_name || ""}</span></div>
                  <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()} · {r.primary_diagnosis || r.chief_complaint || "Consultation"}{r.doctor_name ? ` · Dr. ${r.doctor_name}` : ""}</div>
                </button>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-semibold">{closed ? money(r.amount) : "—"}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${closed ? "bg-emerald-500/15 text-emerald-600" : "bg-amber-500/15 text-amber-600"}`}>{closed ? "Billed" : "Open"}</span>
                  {isAdmin && !closed && <Button size="sm" onClick={() => setCloseRec(r)} data-testid={`consultation-close-${r.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Close</Button>}
                  <a href={pdfUrl(r.id)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid={`consultation-pdf-${r.id}`}><Download className="h-4 w-4" /></Button></a>
                  <Button variant="ghost" size="sm" onClick={() => navigate(`/consultations/${r.id}/edit`)}><Pencil className="h-4 w-4" /></Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      <CloseFlow
        open={!!closeRec}
        onOpenChange={(o) => { if (!o) setCloseRec(null); }}
        title="Close & bill consultation"
        initialAmount={closeRec?.amount ?? ""}
        onConfirmClose={async (amt) => {
          try {
            await api.put(`/consultations/${closeRec.id}/amount`, { amount: amt });
            load();
            return closeRec.id;
          } catch (e) { toast.error("Could not set amount"); throw e; }
        }}
        pdfUrl={pdfUrl}
        onSchedule={async (dateISO) => { await api.post("/appointments", { patient_id: closeRec.patient_id, date_time: dateISO, service_type: "vet" }); }}
        scheduleLabel="next appointment"
      />
    </div>
  );
}
